//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useContext, useEffect, useRef, useState } from 'react';
// import { useState, useEffect, useContext, useCallback } from 'react';
import { makeStyles, Grid } from '@material-ui/core';
import SimpleBar from 'simplebar-react';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useHistory, useParams } from 'react-router';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
// import { unsubscribeRealtimeUpdates } from '../system/firebase';
import { AppContext, LayoutContext } from '../../common/contexts';
import { setClassNameInDefns } from '../../common/definitionHelper';
import { BASE_URL } from '../../common/constants';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------
import { saveEmployerAccountLinkage } from '../../common/services/empAccountLinkageServices';

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdTypography from '../../common/elemental/AfitdTypography';
// import AfitdTypography from '../../common/elemental/AfitdTypography';
import EmployerInviteItem from './EmployerInviteItem';
import AfitdPagination from '../../common/elemental/AfitdPagination';
import AfitdButton from '../../common/elemental/AfitdButton';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
// import cx from 'clsx';
// import _get from 'lodash/get';
import _fpSet from 'lodash/fp/set';
import _map from 'lodash/map';
import _slice from 'lodash/slice';
import _cloneDeep from 'lodash/cloneDeep';
import _some from 'lodash/some';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = makeStyles(theme => ({
    root: { justifyContent: 'center', marginTop: 15 },
    saveButtonContainer: {
        padding: '15px',
        marginTop: 20,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        [theme.breakpoints.down('sm')]: { flexDirection: 'column', marginTop: 0 },
    },
    rootContent1: { textAlign: 'center', paddingBottom: '30px' },
    headerText: { marginBottom: 19, fontWeight: 'bold', fontSize: 26 },
    rootContent2: { height: '100vh', maxHeight: 450 },
    paginationContainer: {
        display: 'flex',
        justifyContent: 'center',
        [theme.breakpoints.down('sm')]: { width: '100%', padding: 15 },
    },
    saveButton: { [theme.breakpoints.down('sm')]: { width: '100%' } },
}));

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const {
        classes,
        // props,
        isLinkageCompleted,
        invitationList,
        handleClickSaveButton,
    } = params;

    const saveButtonDisabled = isLinkageCompleted || _some(invitationList, invitation => invitation.isAccept === undefined);

    const defns = {
        root: { container: true },
        rootContent1: { item: true, xl: 7, lg: 8, md: 9, sm: 11, xs: 11 },
        rootContent2: { item: true, xl: 7, lg: 8, md: 9, sm: 11, xs: 11 },
        title: { variant: 'h5', clsSkinnyBold: true /* clsLightBold: true */ },
        message: { variant: 'body1' },
        saveButton: { color: 'primary', label: 'Save', disabled: saveButtonDisabled, onClick: handleClickSaveButton },
        headerText: { variant: 'h5' },
        linkageText: { variant: 'body1' },
        nullSection: { width: '100%' },
    };

    // Automatically inject className for matching keys
    setClassNameInDefns(defns, classes);

    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------
// const PAGE_ID_EMP_ACCOUNT_LINKAGE = 1;
// const PAGE_ID_NO_OPEN_INVITATIONS = 2;
// const PAGE_ID_NO_LINKED_EMPLOYERS = 3;
const PAGE_ID_ALL_INVITATION_DECLINED = 4;
// const PAGE_ID_BLANK = 5;

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const EmployerAccountLinkage = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const classes = useStyles();
    const appContext = useContext(AppContext);
    const { currentUser, userId } = appContext;

    const layoutContext = useContext(LayoutContext);
    const { setCurrentPage, isLinkageCompleted, setIsLinkageCompleted, setSnackbarMessage, setSnackbarErrorMessage } = layoutContext;

    const theme = useTheme();
    const smMatches = useMediaQuery(theme.breakpoints.down('sm'));

    const history = useHistory();
    const myRef = useRef(null);
    let { pageNumber = 1, subPageId } = useParams();
    pageNumber = parseInt(pageNumber);

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    const { employerInviteList = [] } = props;

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [page, setPage] = React.useState(pageNumber);
    const [invitationList, setInvitationList] = useState([]);

    //---------------------------------------------------------------------
    // pagination section
    //---------------------------------------------------------------------
    const paginationNumber = Math.ceil(invitationList.length / 10);

    const handlePageChange = (event, value) => {
        setPage(value);
        history.push(`${BASE_URL}/tempAuthHandler/${subPageId}/${value}`);
    };
    const start = page * 5 - 5;
    const end = page * 5;
    const invitationListSliced = _slice(invitationList, start, end);

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    useEffect(() => {
        setInvitationList(_map(employerInviteList, o => _cloneDeep(o)));
    }, [employerInviteList]);

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // useCallback / Event handlers
    //---------------------------------------------------------------------
    const handleAcceptInvitation = idx => {
        const newList = _fpSet(`[${idx}].isAccept`, true, invitationList);
        setInvitationList(newList);
    };

    const handleDeclineInvitation = idx => {
        const newList = _fpSet(`[${idx}].isAccept`, false, invitationList);
        setInvitationList(newList);
    };

    const handleClickSaveButton = async () => {
        try {
            await saveEmployerAccountLinkage({ inviteeUserId: userId, inviteeUserData: currentUser, invitationList, setSnackbarMessage, setSnackbarErrorMessage });

            setIsLinkageCompleted(true);

            // if all invitations have been declined, show message.
            const isDeclinedAll = !_some(invitationList, o => o.isAccept);
            if (isDeclinedAll) {
                setCurrentPage(PAGE_ID_ALL_INVITATION_DECLINED);
            }
        } catch (error) {
            console.error('Save Employer Account Linkage failed.', error);
        }
    };

    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    const defns = getDefns({ classes, props, isLinkageCompleted, invitationList, handleClickSaveButton });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <Grid {...defns.root}>
            <Grid {...defns.rootContent1}>
                <AfitdTypography {...defns.headerText}>Employer Account Linkage</AfitdTypography>
                <AfitdTypography {...defns.linkageText}>You have been invited to join the following employer account(s) below:</AfitdTypography>
            </Grid>
            <Grid {...defns.rootContent2}>
                <SimpleBar style={{ height: '100%', width: '100%' }}>
                    <div ref={myRef} />
                    {_map(invitationListSliced, (employerInviteItem, idx) => (
                        <EmployerInviteItem
                            data={employerInviteItem}
                            idx={idx}
                            onAccept={handleAcceptInvitation.bind(null, idx)}
                            onDecline={handleDeclineInvitation.bind(null, idx)}
                        />
                    ))}
                </SimpleBar>
                <div {...defns.saveButtonContainer}>
                    {smMatches ? null : <div {...defns.nullSection}> </div>}
                    <div
                        {...defns.paginationContainer}
                        onClick={() => {
                            setTimeout(() => {
                                myRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                            }, 100);
                        }}
                    >
                        <AfitdPagination count={paginationNumber} color="secondary" page={page} onChange={handlePageChange} />
                    </div>
                    <AfitdButton {...defns.saveButton} />
                </div>
            </Grid>
        </Grid>
    );
};

export default EmployerAccountLinkage;
